import { CatalogEntryRecommendationSkeleton } from '@/components/blocks/CatalogEntryRecommendationSkeleton.tsx';
import { DpCatalogEntryCarouselSeeMore } from '@/components/blocks/DpLibrary/DpCatalogEntryCarouselSeeMore';
import { CatalogEntryRecommendation } from '@/components/content/CatalogEntryRecommendation';
import React, { memo, useEffect, useState } from 'react';

export type DpRenderCatalogsEntryRecommendationCatalog = {
	spot: string;
	moreHref?: string;
	emptySpot?: string;
	name: string;
};

interface Props {
	currentSpot: string;
	emptySpot?: string;
	catalogs: Array<DpRenderCatalogsEntryRecommendationCatalog>;
}

const SmartCatalogEntry = memo(
	({
		catalog,
		isVisible,
		emptySpot,
	}: {
		catalog: Props['catalogs'][0];
		isVisible: boolean;
		emptySpot?: string;
	}) => {
		const [hasLoaded, setHasLoaded] = useState(false);

		const shouldRender = isVisible || hasLoaded;

		useEffect(() => {
			if (shouldRender && !hasLoaded) {
				setHasLoaded(true);
			}
		}, [shouldRender, hasLoaded]);

		const style: React.CSSProperties = {
			gridArea: 'stack',
			opacity: isVisible ? 1 : 0,
			visibility: isVisible ? 'visible' : 'hidden',
			transition: 'opacity 300ms ease-in-out, visibility 300ms ease-in-out',
			zIndex: isVisible ? 1 : 0,
			width: '100%',
		};

		if (!shouldRender) {
			return <div style={{ ...style, minHeight: '300px' }} />;
		}

		return (
			<div style={style}>
				<CatalogEntryRecommendation
					id={catalog.spot}
					properties={{
						emsName: catalog.spot,
					}}
					emptyElement={
						emptySpot ? (
							<CatalogEntryRecommendation
								id={emptySpot}
								properties={{
									emsName: emptySpot,
								}}
								emptyElement={<CatalogEntryRecommendationSkeleton />}
								withoutmargin
							/>
						) : (
							<CatalogEntryRecommendationSkeleton />
						)
					}
					withoutmargin
				/>
				{catalog.moreHref ? (
					<DpCatalogEntryCarouselSeeMore id={catalog.spot} href={catalog.moreHref} />
				) : null}
			</div>
		);
	}
);

SmartCatalogEntry.displayName = 'SmartCatalogEntry';

export const DpRenderCatalogsEntryRecommendation = ({
	catalogs,
	currentSpot,
	emptySpot,
}: Props) => {
	return (
		<div style={{ display: 'grid', gridTemplateAreas: '"stack"' }}>
			{catalogs.map((catalog) => (
				<SmartCatalogEntry
					key={catalog.spot}
					catalog={catalog}
					emptySpot={emptySpot}
					isVisible={catalog.spot === currentSpot}
				/>
			))}
		</div>
	);
};
